import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_CURRENT_ADMIN } from '../graphql/admin';

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data, loading } = useQuery(GET_CURRENT_ADMIN);

  const adminUser = data?.currentAdminUser;

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: '📊', permission: 'analytics.view' },
    { path: '/admin/users', label: 'Users', icon: '👥', permission: 'users.view' },
    { path: '/admin/organizations', label: 'Organizations', icon: '🏢', permission: 'orgs.view' },
    { path: '/admin/promos', label: 'Promo Codes', icon: '🎟️', permission: 'promo.view' },
    { path: '/admin/analytics', label: 'Analytics', icon: '📈', permission: 'analytics.view' },
    { path: '/admin/audit', label: 'Audit Log', icon: '📝', permission: 'audit.view' },
    { path: '/admin/team', label: 'Admin Team', icon: '👨‍💼', permission: 'admin.manage' },
  ];

  const hasPermission = (permission: string) => {
    if (!adminUser?.permissions) return false;
    return adminUser.permissions.includes(permission);
  };

  const filteredNavItems = navItems.filter(item => hasPermission(item.permission));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading admin panel...</div>
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md p-8 bg-white rounded-lg shadow">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have admin access to this panel.
          </p>
          <Link to="/" className="text-blue-600 hover:underline">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gray-900 text-white flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <h1 className="text-xl font-bold">Admin Panel</h1>
          <p className="text-sm text-gray-400 mt-1">{adminUser.fullName}</p>
          <span className="inline-block mt-2 px-2 py-1 text-xs bg-purple-600 rounded">
            {adminUser.role.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <span className="mr-3 text-xl">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <Link
            to="/"
            className="flex items-center px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            <span className="mr-2">←</span>
            <span>Back to App</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}
